/**
 * The org's saved layouts as one shared fetch, read by the Layouts tab and the camera view, so
 * a write in one of them shows in both, and the writes of this session that the list does not
 * show yet.
 *
 * Only a write in this session and the portal monitor reload the list: vms_database keeps the
 * layouts out of its changelog, so a layout that another session changed shows at the next load.
 */

import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';

import { snapshotAtoms } from '../snapshotAtoms';
import { writesAtoms } from '../writesAtoms';
import { api } from '@/api';
import {
    type Layout, type LayoutRow, type LayoutsSnapshot, type LayoutWrite, LayoutWriteKind,
    type LayoutWriteRequest,
} from '@/types/layouts';
import { PortalEvent } from '@/types/portal';
import { type Writes, WriteState } from '@/types/writes';

type LayoutWrites = Writes<LayoutWriteRequest>;

const PENDING: LayoutsSnapshot = {
    layouts: [],
    isLoading: true,
    error: null,
};

const NONE: LayoutsSnapshot = {
    layouts: [],
    isLoading: false,
    error: null,
};

function getRow(id: string, layout: LayoutWriteRequest | Layout, write?: LayoutWrite): LayoutRow {
    const row: LayoutRow = {
        id,
        name: layout.name,
        isVideoWall: layout.isVideoWall,
    };
    return write === undefined ? row : { ...row, write };
}

/**
 * The listed layouts with the writes over them. A delete shows while it saves, and a failed one
 * leaves the layout as listed. A create shows after the listed layouts.
 */
function getLayoutRows(layouts: readonly Layout[], writes: LayoutWrites): LayoutRow[] {
    const rows: LayoutRow[] = [];
    for (const layout of layouts) {
        const write = writes.get(layout.id);
        if (write === undefined) {
            rows.push(getRow(layout.id, layout));
            continue;
        }
        if (write.kind === LayoutWriteKind.Delete && write.state === WriteState.Saved) {
            continue;
        }
        rows.push(getRow(layout.id, write, write));
    }
    const listedIds = new Set(layouts.map((layout) => layout.id));
    for (const [id, write] of writes) {
        if (write.kind === LayoutWriteKind.Create && !listedIds.has(id)) {
            rows.push(getRow(id, write, write));
        }
    }
    return rows;
}

const layouts = snapshotAtoms<LayoutsSnapshot>(
    PENDING,
    async () => ({
        layouts: await api.listLayouts(),
        isLoading: false,
        error: null,
    }),
    {
        onLoaded: (_snapshot, startedAtMs, get, set): void => {
            layoutWrites.endAnswered(get, set, startedAtMs);
        },
    },
);

const layoutWrites = writesAtoms<LayoutWriteRequest>(layouts.reload);

export const layoutsAtom = layouts.value;
export const refreshLayoutsAtom = layouts.reload;

/** What a surface that shows no layout reads in place of the list: it requests nothing. */
export const noLayoutsAtom = atom((): LayoutsSnapshot => NONE);

export const layoutRowsAtom = atom((get): LayoutRow[] =>
    getLayoutRows(get(layoutsAtom).layouts, get(layoutWrites.writes)));

/** The write of one layout. It reads no list, so a reader requests no layouts. */
export const layoutWriteAtom = atomFamily((id: string | null) =>
    atom((get): LayoutWrite | undefined =>
        (id === null ? undefined : get(layoutWrites.writes).get(id))));

export const dropLayoutWriteAtom = layoutWrites.drop;
export const startLayoutWriteAtom = layoutWrites.start;
export const answerLayoutWriteAtom = layoutWrites.answer;

/** A failed delete changed nothing, so its row shows the layout as listed. */
export const failLayoutWriteAtom = atom(null, (get, set, id: string, error: Error): void => {
    if (get(layoutWrites.writes).get(id)?.kind === LayoutWriteKind.Delete) {
        set(layoutWrites.drop, id);
        return;
    }
    set(layoutWrites.fail, id, error);
});

/**
 * What the portal monitor means for the layouts. A load that failed while the cloud was away
 * leaves the list stale.
 */
export const layoutsPortalAtom = atom(null, (_get, set, event: PortalEvent): void => {
    if (event === PortalEvent.Answered) {
        set(layouts.reload);
    }
});
